import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, Typography, Alert, Row, Col, Space, Tabs } from 'antd';
import { UserOutlined, IdcardOutlined, BankOutlined, TeamOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import apiService from "services/api/apiService";
import './Login.css';

const { Title, Text } = Typography;

interface AdminLoginForm {
  adminEmail: string;
  adminPassword: string;
}

interface EmployeeLoginForm {
  email?: string;
  firstName?: string;
  lastName?: string;
  employeeId?: string;
  organizationId?: string;
}

const Login: React.FC = () => {
  const [adminForm] = Form.useForm();
  const [employeeForm] = Form.useForm();
  const { login, employeeLogin, isLoading } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string>('');
  const [activeTab, setActiveTab] = useState<string>('admin');



  const onAdminFinish = async (values: AdminLoginForm) => {
    try {
      setError('');
      await login(values.adminEmail, values.adminPassword);
      const redirect = localStorage.getItem('hr_post_login_redirect') || '/dashboard';
      localStorage.removeItem('hr_post_login_redirect');
      navigate(redirect, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Admin login failed. Please try again.');
    }
  };

  const onEmployeeFinish = async (values: EmployeeLoginForm) => {
    try {
      setError('');
      if (values.email) {
        await employeeLogin('', '', values.email, '');
      } else {
        await employeeLogin(values.firstName || '', values.lastName || '', values.employeeId || '', values.organizationId || '');
      }
      const redirect = localStorage.getItem('hr_post_login_redirect') || '/my-dashboard';
      localStorage.removeItem('hr_post_login_redirect');
      navigate(redirect, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Employee login failed. Please check your credentials.');
    }
  };

  return (
    <div className="login-container">
      <Row justify="center" align="middle" style={{ minHeight: '100vh' }}>
        <Col xs={22} sm={16} md={12} lg={8} xl={6}>
          <Card className="login-card" bordered={false}>
            {/* Header */}
            <div className="login-header">
              <div className="logo">
                <BankOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
              </div>
              <Title level={2} className="login-title">Nexus HR System</Title>
              <Text type="secondary" className="login-subtitle">
                World-class human resources management platform
              </Text>
            </div>

            {/* Error Alert */}
            {error && (
              <Alert
                message="Authentication Failed"
                description={error}
                type="error"
                showIcon
                style={{ marginBottom: 24 }}
              />
            )}

            {/* Tabbed Login Forms */}
            <Tabs 
              activeKey={activeTab} 
              onChange={setActiveTab}
              centered
              style={{ marginBottom: 0 }}
            >
              {/* Admin Login Tab */}
              <Tabs.TabPane 
                tab={
                  <span>
                    <UserOutlined />
                    Admin Login
                  </span>
                } 
                key="admin"
              >
                <Form
                  form={adminForm}
                  name="adminLogin"
                  onFinish={onAdminFinish}
                  layout="vertical"
                  size="large"
                  className="login-form"
                >
                  <Form.Item
                    label="Admin Email"
                    name="adminEmail"
                    rules={[
                      { required: true, message: 'Please enter your email address' },
                      { type: 'email', message: 'Please enter a valid email address' }
                    ]}
                  >
                    <Input
                      prefix={<UserOutlined />}
                      placeholder="Enter your admin email"
                      autoComplete="email"
                      type="email"
                    />
                  </Form.Item>

                  <Form.Item
                    label="Password"
                    name="adminPassword"
                    rules={[
                      { required: true, message: 'Please enter your password' },
                      { min: 6, message: 'Password must be at least 6 characters' }
                    ]}
                  >
                    <Input.Password
                      prefix={<IdcardOutlined />}
                      placeholder="Enter your password"
                      autoComplete="current-password"
                    />
                  </Form.Item>

                  <Form.Item style={{ marginBottom: 0 }}>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={isLoading}
                      block
                      className="login-button"
                    >
                      {isLoading ? 'Signing In...' : 'Sign In as Admin'}
                    </Button>
                  </Form.Item>
                </Form>
              </Tabs.TabPane>

              {/* Employee Login Tab */}
              <Tabs.TabPane 
                tab={
                  <span>
                    <TeamOutlined />
                    Employee Login
                  </span>
                } 
                key="employee"
              >
                <Form
                  form={employeeForm}
                  name="employeeLogin"
                  onFinish={onEmployeeFinish}
                  layout="vertical"
                  size="large"
                  className="login-form"
                >
                  <Form.Item
                    label="Work Email"
                    name="email"
                    rules={[
                      { required: true, message: 'Please enter your work email' },
                      { type: 'email', message: 'Please enter a valid email' }
                    ]}
                  >
                    <Input
                      prefix={<UserOutlined />}
                      placeholder="Enter your work email"
                      autoComplete="email"
                      type="email"
                    />
                  </Form.Item>

                  <Row gutter={12}>
                    <Col span={12}>
                      <Form.Item
                        label="First Name"
                        name="firstName"
                        rules={[
                          { required: true, message: 'Please enter your first name' }
                        ]}
                      >
                        <Input
                          prefix={<UserOutlined />}
                          placeholder="First name"
                          autoComplete="given-name"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Last Name"
                        name="lastName"
                        rules={[
                          { required: true, message: 'Please enter your last name' }
                        ]}
                      >
                        <Input
                          prefix={<UserOutlined />}
                          placeholder="Last name"
                          autoComplete="family-name"
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    label="Employee ID"
                    name="employeeId"
                    rules={[
                      { required: true, message: 'Please enter your employee ID' }
                    ]}
                  >
                    <Input
                      prefix={<IdcardOutlined />}
                      placeholder="Enter your employee ID"
                      autoComplete="username"
                    />
                  </Form.Item>

                  <Form.Item style={{ marginBottom: 0 }}>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={isLoading}
                      block
                      className="login-button"
                    >
                      {isLoading ? 'Signing In...' : 'Sign In as Employee'}
                    </Button>
                  </Form.Item>
                </Form>
              </Tabs.TabPane>
            </Tabs>
            {/* Registration Link */}
            <div style={{ textAlign: 'center', marginTop: 16, marginBottom: 8 }}>
              <Text type="secondary">
                Don't have an account?{' '}
                <Button type="link" onClick={() => navigate('/register')} style={{ padding: 0 }}>
                  Create Account
                </Button>
              </Text>
            </div>

            {/* Footer */}
            <div className="login-footer">
              <Space direction="vertical" align="center" size="small">
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  SOC 2 & ISO 27001 Compliant
                </Text>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  © 2025 Nexus HR System. All rights reserved.
                </Text>
              </Space>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Login;
